import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ProjectReactions, ProjectSidebar, ProjectComments } from "@/components/ProjectEngagement";
import { areaBySlug, BADGE_LABELS, isSeeking } from "@/lib/projectMeta";
import { ArrowLeft, Loader2, Target, CheckCircle2, HelpCircle, Handshake } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ListBlock = ({ title, items, Icon, color }) => (
  items && items.length ? (
    <div className="rounded-3xl border-2 border-[#E5E1D8] bg-white p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color }} />
        <h3 className="font-display text-lg font-black text-[#1A1A1A]">{title}</h3>
      </div>
      <ul className="space-y-2">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-[#3F3F3F] leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
            {t}
          </li>
        ))}
      </ul>
    </div>
  ) : null
);

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setItem(null); setError("");
    fetch(`${API}/api/projects/${slug}`)
      .then((r) => { if (!r.ok) throw new Error("Progetto non trovato."); return r.json(); })
      .then(setItem).catch((e) => setError(e.message));
  }, [slug]);

  if (error) return (
    <Layout><div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <p className="text-[#C15C3D] font-semibold mb-5">{error}</p>
      <Link to="/progetti" className="inline-flex items-center gap-2 text-[#2E4F3B] font-bold underline"><ArrowLeft className="w-4 h-4" />Torna ai progetti</Link>
    </div></Layout>
  );

  if (!item) return (
    <Layout><div className="max-w-3xl mx-auto px-6 py-24 flex items-center justify-center text-[#5C5C5C]"><Loader2 className="w-5 h-5 animate-spin mr-2" />Caricamento…</div></Layout>
  );

  const area = areaBySlug(item.area);
  const AreaIcon = area.icon;
  const fit = item.image_fit === "contain" ? "object-contain p-10 bg-white" : "object-cover";

  return (
    <Layout>
      <article data-testid="project-detail" className="bg-[#FBF9F6] pb-16 sm:pb-24">
        {/* Hero image */}
        <div className="relative aspect-[16/9] sm:aspect-[16/7] overflow-hidden bg-[#1f2d22]">
          {item.image_url && <img src={item.image_url} alt={item.title} className={`w-full h-full ${fit} opacity-90`} />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
          {item.is_concept && (
            <span className="absolute top-5 left-5 rounded-full bg-black/70 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5">Concept · progetto in sviluppo</span>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
            <div className="max-w-6xl mx-auto">
              <span className="inline-flex items-center gap-1.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 mb-3" style={{ backgroundColor: area.color }}>
                <AreaIcon className="w-3 h-3" /> {area.name}
              </span>
              <h1 className="font-display text-3xl sm:text-5xl font-black text-white leading-[1.03] tracking-tight max-w-4xl">{item.title}</h1>
              {item.payoff && <p className="mt-3 text-lg sm:text-2xl font-semibold text-[#E8C9A0] italic leading-snug max-w-3xl">{item.payoff}</p>}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-8 sm:pt-10">
          <Link to={`/progetti/${item.area}`} data-testid="project-back" className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#1A1A1A] mb-8"><ArrowLeft className="w-4 h-4" />{area.name}</Link>

          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            {/* LEFT — content */}
            <div className="lg:col-span-2 space-y-10">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {(item.badges || []).map((b) => (
                  <span key={b} className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${isSeeking(b) ? "bg-[#C15C3D] text-white" : "bg-[#2E4F3B]/10 text-[#2E4F3B]"}`}>{BADGE_LABELS[b] || b}</span>
                ))}
              </div>

              <div>
                <p className="text-lg text-[#1A1A1A] font-semibold leading-snug">{item.summary}</p>
                <div className="mt-6 text-base sm:text-lg text-[#3F3F3F] leading-relaxed whitespace-pre-line">{item.content}</div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <ListBlock title="Obiettivi" items={item.objectives} Icon={Target} color={area.color} />
                <ListBlock title="Risultati già raggiunti" items={item.results} Icon={CheckCircle2} color="#2E4F3B" />
                <ListBlock title="Cosa serve" items={item.needs} Icon={HelpCircle} color="#C15C3D" />
                <ListBlock title="Partner che cerchiamo" items={item.partners_sought} Icon={Handshake} color="#8B5E3C" />
              </div>

              {/* Reactions */}
              <div className="rounded-3xl border-2 border-[#E5E1D8] bg-white p-6">
                <h3 className="font-display text-lg font-black text-[#1A1A1A] mb-4">Cosa ne pensi di questo progetto?</h3>
                <ProjectReactions project={item} />
              </div>

              {/* Comments */}
              <div className="rounded-3xl border-2 border-[#E5E1D8] bg-white p-6 sm:p-8">
                <ProjectComments slug={item.slug} />
              </div>
            </div>

            {/* RIGHT — sticky sidebar */}
            <aside className="lg:col-span-1 lg:sticky lg:top-24">
              <ProjectSidebar project={item} />
            </aside>
          </div>
        </div>
      </article>
    </Layout>
  );
}
